import React from "react";
import Navbar from "./navbar";
import Footer from "./footer";
import "./partner.css";
import {
  Users,
  TrendingUp,
  Headphones,
  ShieldCheck,
  Handshake,
  DollarSign,
  BarChart3,
  Award,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const Partner = () => {
  const benefits = [
    {
      icon: <DollarSign size={45} />,
      title: "Lifetime Recurring Revenue",
      desc: "Earn ongoing commissions from every subscription your referrals maintain – build sustainable income.",
    },
    {
      icon: <TrendingUp size={45} />,
      title: "Zero Upfront Investment",
      desc: "Join free. Focus on referrals while we handle billing, support, and platform reliability.",
    },
    {
      icon: <Handshake size={45} />,
      title: "Dedicated Success Manager",
      desc: "Personal account manager for onboarding, strategy, and priority assistance.",
    },
    {
      icon: <BarChart3 size={45} />,
      title: "Real-Time Performance Dashboard",
      desc: "Track referrals, conversions, renewals, and commissions transparently.",
    },
    {
      icon: <ShieldCheck size={45} />,
      title: "Enterprise-Grade Trust",
      desc: "GST-compliant, secure, and designed exclusively for Indian small & medium businesses.",
    },
    {
      icon: <Users size={45} />,
      title: "Exclusive Resources & Training",
      desc: "Marketing kits, demo scripts, co-branded assets, and partner webinars.",
    },
  ];

  const partnerTypes = [
    "Chartered Accountants & Tax Professionals",
    "Business Consultants & Advisors",
    "Digital & Marketing Agencies",
    "Software Resellers & Distributors",
    "Freelancers Serving MSMEs",
    "Satisfied AssistBuddi Books Users",
  ];

  const tiers = [
    {
      level: "Starter",
      commission: "20–25% Recurring",
      benefits: [
        "Personal referral links & tracking",
        "Basic marketing kit",
        "Email & chat support",
        "Monthly performance reports",
      ],
    },
    {
      level: "Growth",
      commission: "30% Recurring + Bonuses",
      benefits: [
        "Dedicated account manager",
        "Co-branded materials & demos",
        "Priority support & training sessions",
        "Performance-based incentives",
      ],
    },
    {
      level: "Elite",
      commission: "35–40% Recurring + Premium Perks",
      benefits: [
        "Advanced analytics & custom tools",
        "Featured partner spotlight (coming soon)",
        "Marketing development funds",
        "Exclusive events & early access",
      ],
    },
  ];

  const howItWorks = [
    { step: "01", title: "Apply", desc: "Submit your details in 2 minutes" },
    {
      step: "02",
      title: "Approval",
      desc: "Reviewed & approved in 24–48 hours",
    },
    {
      step: "03",
      title: "Onboard",
      desc: "Get your partner portal & resources",
    },
    {
      step: "04",
      title: "Earn",
      desc: "Refer businesses & receive monthly payouts",
    },
  ];

  const testimonials = [
    {
      text: "AssistBuddi Books transformed how my clients handle GST & billing. Referring them is effortless and already generating steady recurring income.",
      author: "CA Rahul Sharma",
      role: "Tax Consultant, Indore",
    },
    {
      text: "Professional platform, excellent support team. My network of shop owners loves the simplicity – perfect side income stream.",
      author: "Priya Verma",
      role: "Business Advisor, Bhopal",
    },
    {
      text: "The dashboard is intuitive, payouts are timely. Ideal for consultants looking to add value without extra work.",
      author: "Amit Patel",
      role: "Freelance Consultant, Mumbai",
    },
  ];

  const faqs = [
    {
      q: "Is joining completely free?",
      a: "Yes – no fees, no investment required at any stage.",
    },
    {
      q: "How are commissions calculated & paid?",
      a: "Percentage of recurring subscription revenue from your referrals, paid monthly via NEFT with full transparency.",
    },
    {
      q: "What kind of support do partners receive?",
      a: "Dedicated manager, training resources, marketing assets, and priority technical help.",
    },
    {
      q: "How quickly can I start referring?",
      a: "Immediately after approval – get links & dashboard access right away.",
    },
    {
      q: "Who is the ideal partner?",
      a: "Anyone advising or serving small businesses, kirana stores, traders, or professionals in India.",
    },
  ];

  return (
    <>
      <Navbar />

      {/* HERO – Professional & Confident */}
      <section className="partner-hero mb-5">
        <div className="partner-hero-overlay"></div>

        <div className="container">
          <div className="partner-hero-grid">
            <div className="partner-hero-content text-center mx-auto">
              <div className="">
                <span className="feature-tag">Official Partner Program</span>
              </div>

              <h1 className="partner-hero-title">
                Partner with{" "}
                <span className="partner-gradient-text">AssistBuddi Books</span>
              </h1>

              <p className="industry-subtitle">
                Empower Indian Businesses • Build Recurring Revenue
              </p>

              <p className="industry-description">
                Join select professionals helping MSMEs simplify accounting, GST
                compliance & business management — while earning sustainable
                commissions with zero risk.
              </p>

              <div className="partner-hero-actions">
                <a href="/login">
                  <button className="cta-btn cta-btn-primary">
                    <span>Apply Now – Free</span>
                    <span className="partner-btn-arrow">→</span>
                  </button>
                </a>
                <a
                  href="https://wa.me/918084378326?text=Hello%2C%20I%20am%20interested%20in%20partnering%20with%20AssistBuddi%20Books"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="cta-btn cta-btn-primary">
                    <span>Join Now</span>
                    <span className="partner-btn-arrow">→</span>
                  </button>
                </a>

                <a href="#benefits">
                  <button className="partner-btn-secondary btn-lg">
                    <span>Learn How It Works</span>
                  </button>
                </a>
              </div>

              <div className="partner-trust d-none d-md-flex">
                <div className="partner-trust-item">
                  <span className="partner-trust-icon">✓</span>
                  <span>No upfront cost</span>
                </div>
                <div className="partner-trust-item">
                  <span className="partner-trust-icon">✓</span>
                  <span>Recurring commissions</span>
                </div>
                <div className="partner-trust-item">
                  <span className="partner-trust-icon">✓</span>
                  <span>Dedicated partner support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES – Quick credibility */}
      <section className="partner-light py-5">
        <div className="container">
          <div className="row text-center g-4 align-items-center">
            <div className="col-md-3 col-6">
              <ShieldCheck size={56} className="mb-2" />
              <p className="fw-bold">GST Compliant & Secure</p>
            </div>
            <div className="col-md-3 col-6">
              <Award size={56} className="mb-2" />
              <p className="fw-bold">Built for Indian MSMEs</p>
            </div>
            <div className="col-md-3 col-6">
              <BarChart3 size={56} className="mb-2" />
              <p className="fw-bold">Transparent Payouts</p>
            </div>
            <div className="col-md-3 col-6">
              <Users size={56} className="mb-2" />
              <p className="fw-bold">Dedicated Partner Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHO CAN JOIN */}
      <section className="partner-section">
        <div className="container">
          {/* Section Header */}
          <div className="section-header text-center mb-5">
            <h2 className="section-title">For Business Service Experts</h2>
            <p className="partner-subtitle">
              Designed for professionals who support, manage, and grow small
              businesses across India.
            </p>
          </div>

          {/* Roles */}
          <div className="row g-4 justify-content-center">
            {partnerTypes.map((type, i) => (
              <div className="col-md-4 col-lg-3" key={i}>
                <div className="partner-role-card">
                  <span className="partner-role-text">{type}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="partner-section bg-light" id="benefits">
        <div className="container">
          <h2 className="section-title text-center mb-5">
            Why They Choose AssistBuddi Books
          </h2>
          <div className="row g-4 text-center text-md-start justify-content-center">
            {benefits.map((item, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <div className="h-100">
                  <div className="box-icon mb-4">{item.icon}</div>
                  <h5 className="fs-4 fw-bold mb-3">{item.title}</h5>
                  <p className="text-muted">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIERS – Professional presentation */}
      <section className="partner-section">
        <div className="container">
          <h2 className="section-title text-center mb-4">
            Partner Levels – Scale Your Earnings
          </h2>
          <p className="text-center text-muted mb-5">
            Start simple and unlock greater rewards as you grow your referral
            network.
          </p>
          <div className="row g-4">
            {tiers.map((tier, i) => (
              <div className="col-md-4" key={i}>
                <div className="partner-card text-center h-100 shadow">
                  <h4 className="fw-bold mb-2" style={{ color: "#0b2b6d" }}>
                    {tier.level}
                  </h4>
                  <h5 className="mb-4 fs-5">{tier.commission}</h5>
                  <ul className="list-unstyled text-start small">
                    {tier.benefits.map((b, j) => (
                      <li key={j} className="mb-3">
                        <CheckCircle2
                          size={18}
                          className="me-2"
                          style={{ color: "#0b2b6d" }}
                        />{" "}
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="partner-section bg-light">
        <div className="container">
          <h2 className="section-title text-center mb-5">
            Simple Path to Partnership
          </h2>
          <div className="row g-4 text-center">
            {howItWorks.map((step, i) => (
              <div className="col-md-3" key={i}>
                <div className="step-card">
                  <div className="step-number fs-4">{step.step}</div>
                  <h5 className="mt-4 mb-2 fs-4 fw-bold">{step.title}</h5>
                  <p className="text-muted ">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="partner-section">
        <div className="container">
          <h2 className="section-title text-center mb-5">
            Trusted by Professionals Across India
          </h2>
          <div className="row g-4">
            {testimonials.map((t, i) => (
              <div className="col-md-4" key={i}>
                <div className="testimonial-card p-4 h-100 shadow">
                  <p className="fst-italic mb-4">"{t.text}"</p>
                  <div>
                    <strong>{t.author}</strong>
                    <p className="text-muted small mb-0">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="partner-light">
        <div className="container">
          <h2 className="section-title text-center mb-5">Common Questions</h2>
          <div className="accordion pb-5" id="faqAccordion">
            {faqs.map((faq, i) => (
              <div className="accordion-item" key={i}>
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#faqCollapse${i}`}
                  >
                    {faq.q}
                  </button>
                </h2>
                <div
                  id={`faqCollapse${i}`}
                  className="accordion-collapse collapse"
                  data-bs-parent="#faqAccordion"
                >
                  <div className="accordion-body">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="partner-cta" id="apply">
        <div className="container text-center">
          <h2 className="mb-4">Ready to Partner with AssistBuddi Books?</h2>
          <p className="lead mb-4">
            Join our growing network of professionals – fast approval, full
            support, lifetime potential.
          </p>
          <a
            href="https://wa.me/918084378326?text=Hello%2C%20I%20am%20interested%20in%20partnering%20with%20AssistBuddi%20Books"
            target="_blank"
            className="btn btn-white mt-3 fw-semibold"
          >
            {/* <img
              src="/assets/img/whatsapp.png"
              alt="WhatsApp"
              width="20"
              class="me-2"
            /> */}
            Join Now
          </a>
          {/* <a href="/contact" className="btn-main btn-lg px-5 py-3">
            Submit Your Application Now
          </a> */}
          <p className="mt-4 small text-white-75">
            Application takes ~2 minutes • Most approvals within 48 hours • No
            commitment required
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Partner;
